import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { TrainDeviation } from '../models/train-deviation.model';
import { DriverDeviationFilter } from '../models/driver-deviation-filter.model';
import { ResolveDeviationRequest } from '../models/resolve-deviation-request.model';
import { DeviationStatus, DriverRole, DriverSelectionType } from '../enums/driver-deviation-enums';
import { DriverInfo } from '../models/driver-info.model';
import { ConflictGroup } from '../models/conflict-group.model';

@Injectable({
  providedIn: 'root'
})
export class DriverDeviationService {
  private requestHeader: HttpHeaders = new HttpHeaders({
    'api-version': environment.driverDeviationApiVersion,
    'app-version': '1.0'
  });

  // State management
  private deviationsSubject = new BehaviorSubject<TrainDeviation[]>([]);
  public deviations$ = this.deviationsSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  /**
   * Get all driver deviations with optional filtering
   * API spec: GET /api/v{version}/driverdeviations
   */
  getDeviations(filter?: DriverDeviationFilter): Observable<TrainDeviation[]> {
    this.loadingSubject.next(true);

    let params = new HttpParams();

    if (filter) {
      const filterMappings: Array<{
        key: keyof DriverDeviationFilter;
        paramName: string;
        transform?: (value: any) => string;
      }> = [
        { key: 'includeHandled', paramName: 'includeHandled', transform: (value: boolean) => value.toString() },
        { key: 'searchTerm', paramName: 'searchTerm' },
        { key: 'trafficDate', paramName: 'trafficDate', transform: (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }}
      ];

      filterMappings.forEach(mapping => {
        const value = filter[mapping.key];

        if (value !== null && value !== undefined) {
          const paramValue = mapping.transform ? mapping.transform(value) : value.toString();
          params = params.append(mapping.paramName, paramValue);
        }
      });
    }

    const url = `${environment.driverDeviationBaseUrl}${environment.getDriverDeviationsApiSuffix}`;

    return this.httpClient.get<any[]>(url, {
      headers: this.requestHeader,
      params: params
    }).pipe(
      map(response => this.mapResponseToDeviations(response)),
      map(deviations => {
        this.deviationsSubject.next(deviations);
        this.loadingSubject.next(false);
        return deviations;
      })
    );
  }


  resolveDeviation(deviationId: string, request: ResolveDeviationRequest): Observable<void> {
    const url = `${environment.driverDeviationBaseUrl}${environment.resolveDeviationApiSuffix}/${deviationId}/resolve`;

    return this.httpClient.patch<any>(url, request, {
      headers: this.requestHeader
    }).pipe(
      map(() => {
        // API returns null or empty on success
        return;
      })
    );
  }

  /**
   * Map API response to domain models
   * Backend structure: DriverDeviationV2 with conflictGroups
   */
  private mapResponseToDeviations(response: any[]): TrainDeviation[] {
    return response.map(item => {
      const status = this.mapStatus(item.status);

      const conflictGroups: ConflictGroup[] = (item.conflictGroups || []).map((group: any) =>
        new ConflictGroup(
          group.id,
          {
            origin: this.normalizeStation(group.route?.origin),
            destination: this.normalizeStation(group.route?.destination),
            departureAtUtc: group.route?.departureAtUtc ? new Date(group.route.departureAtUtc) : new Date(),
            arrivalAtUtc: group.route?.arrivalAtUtc ? new Date(group.route.arrivalAtUtc) : new Date()
          },
          this.mapDrivers(group.trainDrivers || [])
        )
      );

      // A deviation is considered handled if:
      // 1. Status is DEVIATION_HANDLED, OR
      // 2. A driver has been selected in any conflict group
      const hasSelectedDriver = conflictGroups.some(g =>
        g.trainDrivers.some(d => d.selection === DriverSelectionType.SELECTED || d.isSelected === true)
      );
      const isHandled = status === DeviationStatus.DEVIATION_HANDLED || hasSelectedDriver;

      return new TrainDeviation(
        item.id,
        item.trainNumber,
        item.trafficDate,
        {
          origin: this.normalizeStation(item.route?.origin),
          destination: this.normalizeStation(item.route?.destination),
          departureAtUtc: item.route?.departureAtUtc ? new Date(item.route.departureAtUtc) : new Date(),
          arrivalAtUtc: item.route?.arrivalAtUtc ? new Date(item.route.arrivalAtUtc) : new Date()
        },
        status,
        conflictGroups,
        item.note || '',
        isHandled,
        item.lastUpdated ? new Date(item.lastUpdated) : undefined
      );
    });
  }

  /**
   * Map API status to enum
   */
  private mapStatus(status: string | number): DeviationStatus {
    // Backend sends enum as number, convert to string name
    const statusStr = typeof status === 'number'
      ? ['NEW_TRAIN', 'CONFLICT', 'DEVIATION_HANDLED'][status]
      : status?.toUpperCase();

    switch (statusStr) {
      case 'NEW_TRAIN':
      case '0':
        return DeviationStatus.NEW_TRAIN;
      case 'CONFLICT':
      case '1':
        return DeviationStatus.CONFLICT;
      case 'DEVIATION_HANDLED':
      case 'HANDLED':
      case '2':
        return DeviationStatus.DEVIATION_HANDLED;
      default:
        return DeviationStatus.CONFLICT;
    }
  }

  /**
   * Map API driver data to DriverInfo models
   * Backend structure: TrainDriverV2
   */
  private mapDrivers(drivers: any[]): DriverInfo[] {
    if (!drivers || !Array.isArray(drivers)) {
      return [];
    }

    return drivers.map(driver => new DriverInfo(
      driver.firstName || '',
      driver.lastName || '',
      this.mapDriverRole(driver.role),
      {
        origin: this.normalizeStation(driver.route?.origin),
        destination: this.normalizeStation(driver.route?.destination),
        departureAtUtc: driver.route?.departureAtUtc ? new Date(driver.route.departureAtUtc) : new Date(),
        arrivalAtUtc: driver.route?.arrivalAtUtc ? new Date(driver.route.arrivalAtUtc) : new Date()
      },
      driver.id,
      driver.userId,
      driver.employeeId,
      driver.phoneNumber,
      this.mapDriverSelection(driver.selection),
      driver.isSelected
    ));
  }

  private normalizeStation(name: string | null | undefined): string {
    if (!name) return '';
    return name
      .trim()
      .toUpperCase()
      .replace(/\s+\d+$/, '');
  }

  /**
   * Map API driver role to enum
   */
  private mapDriverRole(role: string | number): DriverRole {
    // Backend may send enum as number
    const roleStr = typeof role === 'number'
      ? ['PLANNED', 'DEVIATING'][role]
      : role?.toUpperCase();

    switch (roleStr) {
      case 'PLANNED':
      case '0':
        return DriverRole.PLANNED;
      case 'DEVIATING':
      case '1':
        return DriverRole.DEVIATING;
      default:
        return DriverRole.PLANNED;
    }
  }

  /**
   * Map API driver selection type to enum
   */
  private mapDriverSelection(selection: string | number): DriverSelectionType {
    // Backend may send enum as number
    const selectionStr = typeof selection === 'number'
      ? ['NOT_EVALUATED', 'SELECTED', 'NOT_SELECTED'][selection]
      : selection?.toUpperCase();

    switch (selectionStr) {
      case 'NOT_EVALUATED':
      case '0':
        return DriverSelectionType.NOT_EVALUATED;
      case 'SELECTED':
      case '1':
        return DriverSelectionType.SELECTED;
      case 'NOT_SELECTED':
      case '2':
        return DriverSelectionType.NOT_SELECTED;
      default:
        return DriverSelectionType.NOT_EVALUATED;
    }
  }

  /**
   * Get current deviations from subject
   */
  getCurrentDeviations(): TrainDeviation[] {
    return this.deviationsSubject.value;
  }

  /**
   * Update a specific deviation in the local state
   */
  updateDeviation(updatedDeviation: TrainDeviation): void {
    const currentDeviations = this.deviationsSubject.value;
    const index = currentDeviations.findIndex(d => d.id === updatedDeviation.id);

    if (index !== -1) {
      currentDeviations[index] = updatedDeviation;
      this.deviationsSubject.next([...currentDeviations]);
    }
  }
}
